import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL || '';

/**
 * Payment API Service
 * Handles all monetization-related API calls
 */

export default {
  /**
   * Get parallelium packages by locale
   * @param {string} locale - Locale (pt, en, es, zh)
   * @returns {Promise<Array>} List of packages
   */
  async getParalleliumPackages(locale = 'pt') {
    try {
      const response = await axios.get(`${API_URL}/api/parallelium-packages`, {
        params: { locale }
      });
      return response.data.data;
    } catch (error) {
      // If 404, return empty array (no packages configured yet)
      if (error.response?.status === 404) {
        console.warn('No parallelium packages found');
        return [];
      }
      console.error('Error fetching parallelium packages:', error);
      throw error;
    }
  },

  /**
   * Get all active promotions
   * @returns {Promise<Array>} List of active promotions
   */
  async getActivePromotions() {
    try {
      const response = await axios.get(`${API_URL}/api/promotions/active`);
      return response.data.data;
    } catch (error) {
      // If 404, return empty array (no active promotions)
      if (error.response?.status === 404) {
        console.warn('No active promotions found');
        return [];
      }
      console.error('Error fetching active promotions:', error);
      throw error;
    }
  },

  /**
   * Get sidebar promotions (displayInSidebar = true)
   * @returns {Promise<Array>} List of sidebar promotions
   */
  async getSidebarPromotions() {
    try {
      const response = await axios.get(`${API_URL}/api/promotions/sidebar`);
      return response.data.data;
    } catch (error) {
      // If 404, return empty array (no sidebar promotions)
      if (error.response?.status === 404) {
        console.warn('No sidebar promotions found');
        return [];
      }
      console.error('Error fetching sidebar promotions:', error);
      throw error;
    }
  },

  /**
   * Get popup promotion (showPopup = true)
   * @returns {Promise<Object|null>} Popup promotion or null
   */
  async getPopupPromotion() {
    try {
      const response = await axios.get(`${API_URL}/api/promotions/popup`);
      return response.data.data;
    } catch (error) {
      // If 404, return null (no popup promotion)
      if (error.response?.status === 404) {
        console.warn('No popup promotion found');
        return null;
      }
      console.error('Error fetching popup promotion:', error);
      throw error;
    }
  },

  /**
   * Create Stripe checkout session
   * @param {number} packageId - Package ID to purchase
   * @param {string} token - Auth token
   * @returns {Promise<Object>} Session data with URL
   */
  async createCheckoutSession(packageId, token) {
    try {
      const response = await axios.post(
        `${API_URL}/api/payment/create-checkout-session`,
        { packageId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  /**
   * Verify purchase status
   * @param {string} sessionId - Stripe session ID
   * @param {string} token - Auth token
   * @returns {Promise<Object>} Purchase data
   */
  async verifyPurchase(sessionId, token) {
    try {
      const response = await axios.get(
        `${API_URL}/api/payment/verify/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error verifying purchase:', error);
      throw error;
    }
  },

  /**
   * Get player purchase history
   * @param {string} token - Auth token
   * @returns {Promise<Array>} Purchase history
   */
  async getPurchaseHistory(token) {
    try {
      const response = await axios.get(`${API_URL}/api/parallelium-purchases/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      throw error;
    }
  }
};
